import React from 'react';
import { Badge, IconButton } from '@parsina/framework';
import { Copy, Check, RefreshCw, ThumbsUp, ThumbsDown } from 'lucide-react';

import { C, CT, soft } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { Markdown, markdownToPlainText } from '../../lib/markdown.js';
import { CostChip } from '../common/ParsehCost.jsx';
import { AiAnswerNote } from '../common/PracticeNotice.jsx';
import { HoverRevealStyle, HOVER_HOST, HOVER_ACTIONS } from './hoverReveal.jsx';

// The assistant's turn — a DOCUMENT, not a bubble. core.md §9.3.
//
// A teaching answer is three paragraphs, a list of differentials and sometimes a
// table; a chat bubble sized to its content would put a 400-word answer in a
// rounded box with a tail, and the reader would lose the line every time it
// wrapped. So the assistant's turn is full-width prose on the page surface,
// exactly as ChatGPT renders one, and only the STUDENT's turn is a bubble.
//
// ── Three things this component must not do ─────────────────────────────
//
//   1. It must not render raw HTML. `lib/markdown.js` escapes it by
//      construction (every run becomes a React text node), which is why the
//      renderer is ours and not a dependency.
//   2. It must not turn a model's link into an anchor. `[x](url)` renders as
//      «x» — a model does not get to navigate the student's browser.
//   3. **👍/👎 are LOCAL.** There is no feedback field in the schema, no
//      mutation to carry one, and this component never sends one. They are a
//      reader's own note to themselves for the length of the page — a rating
//      quietly posted to a server the student did not choose is worse than no
//      rating at all.

const CONFIDENCE_TONE = { low: 'warning', medium: 'info', high: 'success' };

export default function AssistantMessage({
  message,
  revealText = null,
  confidence = null,
  showAnswerNote = false,
  onRegenerate,
}) {
  const { t, formatTime } = useTranslation();
  const [copied, setCopied] = React.useState(false);
  // Never sent anywhere. See the header.
  const [vote, setVote] = React.useState(null);

  const body = revealText != null ? revealText : (message?.content || '');
  const cost = Number(message?.costParseh || 0);

  const copy = React.useCallback(() => {
    const text = markdownToPlainText(message?.content || '');
    try {
      navigator?.clipboard?.writeText?.(text);
    } catch {
      /* a browser that refuses the clipboard must not take the page down */
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, [message]);

  return (
    <article
      data-testid="assistant-message"
      data-message-id={message?.id}
      className={HOVER_HOST}
      style={{
        alignSelf: 'stretch',
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: '14px 16px 10px',
        color: C.textPrimary,
      }}
    >
      <div style={{ fontSize: 11.5, fontWeight: 700, color: CT.mute, marginBottom: 6 }}>
        {t('chat.message.assistant')}
      </div>

      <Markdown
        text={body}
        labels={{ copy: t('common.actions.copy'), copied: t('common.actions.copied') }}
        onCopy={(code) => { try { navigator?.clipboard?.writeText?.(code); } catch { /* ignore */ } }}
      />

      {showAnswerNote ? <AiAnswerNote /> : null}

      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          marginTop: 10, paddingTop: 8, borderTop: `1px solid ${soft(C.border, 0.6)}`,
        }}
      >
        <span style={{ fontSize: 11.5, color: CT.mute }}>
          {formatTime(message?.createdAtMs)}
        </span>

        {confidence ? (
          <Badge variant={CONFIDENCE_TONE[confidence] || 'info'} data-testid="confidence-pill">
            {`${t('chat.confidenceLabel')}: ${t(`chat.confidence.${confidence}`)}`}
          </Badge>
        ) : null}

        {/* The price is a DISPLAY COPY of what the answer cost; the wallet ledger
            is the authority, and this never sums into a balance (core.md §3.5). */}
        {cost ? <CostChip cost={cost} /> : null}

        <span style={{ flex: 1 }} />

        <HoverRevealStyle />
        <span className={HOVER_ACTIONS} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <IconButton
            size={30}
            variant="ghost"
            icon={copied ? <Check size={15} aria-hidden /> : <Copy size={15} aria-hidden />}
            label={copied ? t('chat.message.copied') : t('chat.message.copy')}
            onClick={copy}
            data-testid="assistant-copy"
          />
          <IconButton
            size={30}
            variant="ghost"
            icon={<RefreshCw size={15} aria-hidden />}
            label={t('chat.message.regenerate')}
            onClick={() => onRegenerate?.(message)}
            data-testid="assistant-regenerate"
          />
          <IconButton
            size={30}
            variant={vote === 'up' ? 'outline' : 'ghost'}
            icon={<ThumbsUp size={15} aria-hidden />}
            label={t('chat.message.thumbsUp')}
            onClick={() => setVote((v) => (v === 'up' ? null : 'up'))}
            data-testid="assistant-thumbs-up"
          />
          <IconButton
            size={30}
            variant={vote === 'down' ? 'outline' : 'ghost'}
            icon={<ThumbsDown size={15} aria-hidden />}
            label={t('chat.message.thumbsDown')}
            onClick={() => setVote((v) => (v === 'down' ? null : 'down'))}
            data-testid="assistant-thumbs-down"
          />
        </span>
      </div>
    </article>
  );
}
